import { useCallback, useEffect, useState } from 'react'
import { storageService } from '../services/storage'

const RESEND_INTERVALS = {
  ONE_MINUTE: 60,
  FIVE_MINUTES: 300,
  SIXTY_MINUTES: 3600,
}

const intervals = [
  RESEND_INTERVALS.ONE_MINUTE,
  RESEND_INTERVALS.FIVE_MINUTES,
  RESEND_INTERVALS.SIXTY_MINUTES,
]

const RESEND_STORAGE_KEY = (to: string) => `resend_timer_data_${to}`

export const useResendTimer = (resendTo: string) => {
  const { setItem, getItem, removeItem } = storageService

  const [lastResendTime, setLastResendTime] = useState<Date | null>(null)
  const [remainingTime, setRemainingTime] = useState<number>(0)
  const [canRequestResend, setCanRequestResend] = useState(true)
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0)

  const saveState = useCallback(
    (resendTo: string, lastResendTime: Date | null, intervalIndex: number) => {
      const state = {
        resendTo,
        lastResendTime: lastResendTime?.toISOString(),
        currentIntervalIndex: intervalIndex,
      }
      setItem(RESEND_STORAGE_KEY(resendTo), JSON.stringify(state))
    },
    [setItem],
  )

  const startTimer = useCallback(() => {
    const now = new Date()
    setLastResendTime(now)
    setCanRequestResend(false)
    setRemainingTime(intervals[currentIntervalIndex])
    saveState(resendTo, now, currentIntervalIndex)
  }, [currentIntervalIndex, resendTo, saveState])

  useEffect(() => {
    const savedState = getItem<string>(RESEND_STORAGE_KEY(resendTo))
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState)
        if (parsedState.resendTo === resendTo) {
          const savedTime = new Date(parsedState.lastResendTime)
          const now = new Date()
          const timeDiff = (now.getTime() - savedTime.getTime()) / 1000
          const currentInterval = intervals[parsedState.currentIntervalIndex]

          setCurrentIntervalIndex(parsedState.currentIntervalIndex)

          if (timeDiff < currentInterval) {
            setLastResendTime(savedTime)
            setCanRequestResend(false)
            setRemainingTime(
              Math.max(currentInterval - Math.floor(timeDiff), 0),
            )
          } else {
            const nextIndex = Math.min(
              parsedState.currentIntervalIndex + 1,
              intervals.length - 1,
            )
            setCurrentIntervalIndex(nextIndex)
            setCanRequestResend(true)
            setRemainingTime(0)
            saveState(resendTo, null, nextIndex)
          }
        }
      } catch (e) {
        console.error('Failed to parse saved resend state', e)
        removeItem(RESEND_STORAGE_KEY(resendTo))
      }
    }
  }, [resendTo, getItem, saveState, removeItem])

  useEffect(() => {
    if (!canRequestResend && lastResendTime) {
      const timer = setInterval(() => {
        const currentTime = new Date()
        const timeDiff =
          (currentTime.getTime() - lastResendTime.getTime()) / 1000
        const currentInterval = intervals[currentIntervalIndex]
        const timeLeft = Math.max(currentInterval - Math.floor(timeDiff), 0)

        setRemainingTime(timeLeft)

        if (timeLeft === 0) {
          const nextIndex = Math.min(
            currentIntervalIndex + 1,
            intervals.length - 1,
          )
          setCurrentIntervalIndex(nextIndex)
          setCanRequestResend(true)
          saveState(resendTo, null, nextIndex)
          clearInterval(timer)
        }
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [
    lastResendTime,
    canRequestResend,
    currentIntervalIndex,
    resendTo,
    saveState,
  ])

  const getCurrentInterval = () => {
    return intervals[currentIntervalIndex]
  }

  return {
    remainingTime,
    canRequestResend,
    startTimer,
    currentInterval: getCurrentInterval(),
    isFinalInterval: currentIntervalIndex === intervals.length - 1,
  }
}
